import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApiError,
  cancelActivation,
  fetchActiveNumbers,
  finishActivation,
  getApiErrorMessage,
  requestAnotherSms,
} from '../services/smsApi';
import { ActiveNumberItem, NumberStatus } from '../types/models';
import { formatDateTime, formatUsd } from '../utils/format';
import { presentPhoneNumber } from '../utils/phone';
import { CountryMark, ServiceMark, SmsIcon } from '../components/sms/SmsMarks';
import LongRentalNumbersSection from '../components/longRent/LongRentalNumbersSection';
import VoiceVerificationNumbersSection from '../components/voice/VoiceVerificationNumbersSection';

interface ActiveNumbersProps {
  numbers: ActiveNumberItem[];
  onActiveNumbersUpdate?: (items: ActiveNumberItem[]) => void;
  onDashboardRefresh?: () => Promise<void> | void;
  mode?: 'mock' | 'live';
}

type ActionName = 'request' | 'finish' | 'cancel';

const FINAL_STATUSES = new Set<NumberStatus>(['finished', 'cancelled', 'expired', 'not_found']);
const RETRY_STATUSES = new Set<NumberStatus>(['received', 'waiting_retry', 'waiting_sms']);
const FINISH_STATUSES = new Set<NumberStatus>(['received', 'waiting_sms', 'waiting_retry']);

function statusLabel(status: NumberStatus): string {
  const map: Record<NumberStatus, string> = {
    allocated: 'Выдан',
    waiting_sms: 'Ожидаем СМС',
    waiting_retry: 'Ожидаем повторное СМС',
    waiting_resend: 'Ждём повторной отправки',
    received: 'СМС получено',
    finished: 'Завершено',
    cancelled: 'Отменено',
    expired: 'Истёк',
    not_found: 'Не найден',
  };
  return map[status] ?? status;
}

function actionErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 404) return 'Активация не найдена';
    if (error.status === 409) return 'Действие недоступно в текущем статусе';
    if (error.status === 502) return 'Поставщик отклонил действие';
  }
  return getApiErrorMessage(error);
}

function getRemainingMs(expiresAt: string, now: number): number {
  const expiresMs = new Date(expiresAt).getTime();
  if (!Number.isFinite(expiresMs)) return 0;
  return Math.max(0, expiresMs - now);
}

function formatRemaining(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function formatActivationDuration(purchasedAt: string, expiresAt: string): string {
  const purchasedMs = new Date(purchasedAt).getTime();
  const expiresMs = new Date(expiresAt).getTime();
  if (!Number.isFinite(purchasedMs) || !Number.isFinite(expiresMs) || expiresMs <= purchasedMs) {
    return '—';
  }

  const totalMinutes = Math.round((expiresMs - purchasedMs) / 60000);
  if (totalMinutes < 60) return `${totalMinutes} мин.`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours} ч. ${minutes} мин.` : `${hours} ч.`;
}

function mergeActivation(items: ActiveNumberItem[], updated: ActiveNumberItem): ActiveNumberItem[] {
  const index = items.findIndex((item) => item.id === updated.id);
  if (index === -1) return [updated, ...items];

  return [
    ...items.slice(0, index),
    updated,
    ...items.slice(index + 1),
  ];
}

function fallbackCopy(value: string): boolean {
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', 'readonly');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  document.body.appendChild(textarea);
  textarea.select();

  try {
    return document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
}

const ActiveNumbers: React.FC<ActiveNumbersProps> = ({
  numbers,
  onActiveNumbersUpdate,
  onDashboardRefresh,
  mode = 'live',
}) => {
  const [items, setItems] = useState<ActiveNumberItem[]>(numbers);
  const [view, setView] = useState<'cards' | 'table'>(() => {
    try { return localStorage.getItem('sms.activeNumbers.view') === 'table' ? 'table' : 'cards'; }
    catch { return 'cards'; }
  });
  const [now, setNow] = useState<number>(() => Date.now());
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isRefreshingAll, setIsRefreshingAll] = useState<boolean>(false);
  const [pendingById, setPendingById] = useState<Record<string, ActionName | undefined>>({});
  const [errorById, setErrorById] = useState<Record<string, string | undefined>>({});
  const [noticeById, setNoticeById] = useState<Record<string, string | undefined>>({});
  const itemsRef = useRef<ActiveNumberItem[]>(numbers);
  const scrolledAnchor = useRef('');
  const isMockMode = mode === 'mock';

  const changeView = (next: 'cards' | 'table') => {
    setView(next);
    try { localStorage.setItem('sms.activeNumbers.view', next); } catch { /* Настройка необязательна. */ }
  };

  const applyItems = useCallback((nextItems: ActiveNumberItem[]) => {
    itemsRef.current = nextItems;
    setItems(nextItems);
    onActiveNumbersUpdate?.(nextItems);
  }, [onActiveNumbersUpdate]);

  const applyUpdatedActivation = useCallback((updated: ActiveNumberItem) => {
    applyItems(mergeActivation(itemsRef.current, updated));
  }, [applyItems]);

  useEffect(() => {
    itemsRef.current = numbers;
    setItems(numbers);
  }, [numbers]);

  useEffect(() => {
    const anchor = window.location.hash.slice(1);
    if (!/^number-\d+$/.test(anchor) || scrolledAnchor.current === anchor) return;
    const card = document.getElementById(anchor);
    if (card) { card.scrollIntoView?.({ block: 'center' }); scrolledAnchor.current = anchor; }
  }, [items]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let isFirstLoad = true;

    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const schedule = () => {
      clearTimer();
      if (!cancelled) {
        timer = setTimeout(tick, 5000);
      }
    };

    const tick = async () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      try {
        const nextItems = await fetchActiveNumbers();
        if (!cancelled) {
          applyItems(nextItems);
          setLoadError(null);
        }
      } catch (error) {
        if (!cancelled && isFirstLoad && itemsRef.current.length === 0) {
          setLoadError(getApiErrorMessage(error));
        }
      } finally {
        isFirstLoad = false;
        schedule();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        clearTimer();
        tick();
      } else {
        clearTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    tick();

    return () => {
      cancelled = true;
      clearTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [applyItems]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = () => {
      if (cancelled) return;
      setNow(Date.now());
      timer = setTimeout(tick, 1000);
    };

    timer = setTimeout(tick, 1000);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const refreshAll = useCallback(async () => {
    setIsRefreshingAll(true);
    setLoadError(null);

    try {
      const nextItems = await fetchActiveNumbers();
      applyItems(nextItems);
    } catch (error) {
      setLoadError(getApiErrorMessage(error));
    } finally {
      setIsRefreshingAll(false);
    }
  }, [applyItems]);

  const runAction = useCallback(async (
    item: ActiveNumberItem,
    action: ActionName,
    request: (id: string) => Promise<ActiveNumberItem>,
  ) => {
    setPendingById((current) => ({ ...current, [item.id]: action }));
    setErrorById((current) => ({ ...current, [item.id]: undefined }));
    setNoticeById((current) => ({ ...current, [item.id]: undefined }));

    try {
      const updated = await request(item.id);
      applyUpdatedActivation(updated);
      const codeArrived = action === 'cancel' && (updated.receivedSmsCodes.length > 0 || Boolean(updated.smsCode));
      const notice = codeArrived
        ? 'СМС уже пришло — отмена не выполнена, код показан в карточке.'
        : (action === 'cancel' && updated.refundOutcomeCode === 'cancel_deferred' ? 'Отмена запланирована' : 'Статус обновлён');
      setNoticeById((current) => ({ ...current, [item.id]: notice }));
      if (action === 'finish' || action === 'cancel') {
        await onDashboardRefresh?.();
      }
    } catch (error) {
      setErrorById((current) => ({ ...current, [item.id]: actionErrorMessage(error) }));
    } finally {
      setPendingById((current) => ({ ...current, [item.id]: undefined }));
    }
  }, [applyUpdatedActivation, onDashboardRefresh]);

  const handleCancel = useCallback((item: ActiveNumberItem) => {
    if (!window.confirm('Отменить активацию? Зачисление будет выполнено только после подтверждения возврата поставщиком.')) {
      return;
    }

    runAction(item, 'cancel', cancelActivation);
  }, [runAction]);

  const handleCopy = useCallback(async (
    item: ActiveNumberItem,
    value: string,
    successMessage = 'Скопировано',
  ) => {
    if (!value || value === '—') return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else if (!fallbackCopy(value)) {
        throw new Error('Буфер обмена недоступен');
      }
      setNoticeById((current) => ({ ...current, [item.id]: successMessage }));
      window.setTimeout(() => {
        setNoticeById((current) => ({ ...current, [item.id]: undefined }));
      }, 1600);
    } catch (error) {
      setErrorById((current) => ({ ...current, [item.id]: getApiErrorMessage(error) }));
    }
  }, []);

  const renderNumber = (item: ActiveNumberItem, asRow = false) => {
    const remainingMs = getRemainingMs(item.expiresAt, now);
    const isExpiredByTime = remainingMs <= 0;
    const isFinal = FINAL_STATUSES.has(item.status);
    const isCurrent = !isExpiredByTime && !isFinal;
    const isBusy = Boolean(pendingById[item.id]);
    const supportsLifecycleActions = item.supportsLifecycleActions !== false;
    const canRequestAgain = supportsLifecycleActions && RETRY_STATUSES.has(item.status) && isCurrent;
    const canFinish = supportsLifecycleActions && FINISH_STATUSES.has(item.status) && isCurrent;
    const canCancel = (item.canCancel ?? !isFinal) && isCurrent;
    // Отмена в первые минуты откладывается поставщиком и выполняется фоновым обработчиком сама.
    const cancelScheduled = ['requested', 'deferred', 'in_flight'].includes(item.cancellationState || 'none');
    const isRefunded = item.refundOutcomeCode === 'refunded' || item.customerRefundState === 'credited';
    const codes = item.receivedSmsCodes.length > 0
      ? item.receivedSmsCodes
      : (item.smsCode ? [item.smsCode] : []);
    const messages = item.receivedSmsMessages || [];
    const serviceTitle = item.serviceName || item.serviceCode;
    const countryTitle = item.countryName || item.countryCode;
    const phone = presentPhoneNumber(item.phoneNumber);
    const refundLabel = item.refundOutcomeCode === 'cancel_deferred' && item.cancelEligibleAt
      ? `Отмена будет выполнена автоматически через ${formatRemaining(Math.max(0, new Date(item.cancelEligibleAt).getTime() - now))}`
      : item.refundOutcomeLabel;
    const phoneControls = <div className="sms-activeCard__phone">
      <span>{phone.displayFullNumber}</span>
      <span className="sms-activeCard__phoneActions">
        <button type="button" className="sms-copyButton" title="Скопировать номер с кодом страны" onClick={() => handleCopy(item, phone.fullNumber, 'Номер с кодом страны скопирован')} disabled={!phone.fullNumber} aria-label="Скопировать номер с кодом страны"><SmsIcon name="copy" />С кодом</button>
        <button type="button" className="sms-copyButton" title="Скопировать номер без кода страны" onClick={() => handleCopy(item, phone.numberWithoutCountryCode, 'Номер без кода страны скопирован')} disabled={!phone.numberWithoutCountryCode} aria-label="Скопировать номер без кода страны"><SmsIcon name="copy" />Без кода</button>
      </span>
    </div>;

    if (asRow) return <tr key={item.id} id={`number-${phone.fullNumber.replace(/\D/g, '')}`}>
      <td><div className="sms-numberTable__entity"><ServiceMark code={item.serviceCode} name={serviceTitle} /><span>{serviceTitle}</span></div></td>
      <td><div className="sms-numberTable__entity"><CountryMark name={countryTitle} /><span>{countryTitle}</span></div></td>
      <td>{phoneControls}
        {noticeById[item.id] ? <span className="is-positive" role="status">{noticeById[item.id]}</span> : null}
        {errorById[item.id] ? <span className="is-negative" role="alert">{errorById[item.id]}</span> : null}
      </td>
      <td>{isRefunded ? 'Возврат выполнен' : <div className="sms-numberTable__sms" aria-label={`Полученные СМС для номера ${phone.displayFullNumber}`}>
        {messages.length > 0 ? messages.map((message) => <div key={message.id}>
          {message.code ? <button type="button" className="sms-codeButton" onClick={() => handleCopy(item, message.code || '')} aria-label={`Скопировать код ${message.code}`}><SmsIcon name="copy" />{message.code}</button> : null}
          <p>{message.text}</p>
        </div>) : codes.length > 0 ? codes.map(code => <button key={code} type="button" className="sms-codeButton" onClick={() => handleCopy(item, code)} aria-label={`Скопировать СМС-код ${code}`}><SmsIcon name="copy" />{code}</button>) : <span className="u2-muted">Ожидаем СМС</span>}
      </div>}</td>
      <td><span className="sms-countdown" aria-label="Осталось времени">{formatRemaining(remainingMs)}</span></td>
    </tr>;

    return (
      <article key={item.id} id={`number-${phone.fullNumber.replace(/\D/g, '')}`} className={`sms-activeCard${messages.length > 0 ? ' has-sms' : ''}`}>
        <div className="sms-activeCard__top">
          <div className="sms-activeCard__identity">
            <div className="sms-activeCard__marks">
              <ServiceMark code={item.serviceCode} name={serviceTitle} />
              <CountryMark name={countryTitle} />
            </div>
            <div>
              {phoneControls}
              <div className="sms-activeCard__service">{serviceTitle} · {countryTitle}</div>
            </div>
          </div>

          <div className="sms-activeCard__state">
            <span className={`sms-status is-${item.status}`}>{statusLabel(item.status)}</span>
            {isCurrent ? <span className="sms-countdown" aria-label="Осталось времени">{formatRemaining(remainingMs)}</span> : <span>{isExpiredByTime ? 'Срок истёк' : 'Завершён досрочно'}</span>}
          </div>
        </div>

        {!isRefunded ? <section className="sms-inbox" aria-label={`Полученные СМС для номера ${phone.displayFullNumber}`}>
          <div className="sms-inbox__head">
            <div><strong>СМС</strong>{messages.length > 0 ? <span>{messages.length} сообщ.</span> : null}</div>
            {codes.length > 0 && messages.length === 0 ? <div className="sms-inbox__codes">{codes.map((code) => (
              <button key={`${item.id}-${code}`} type="button" className="sms-codeButton" onClick={() => handleCopy(item, code)} aria-label={`Скопировать СМС-код ${code}`}><SmsIcon name="copy" />{code}</button>
            ))}</div> : null}
          </div>
          {messages.length === 0 ? (
            <div className="sms-inbox__waiting">
              {isCurrent ? <span className="sms-waitingDot" aria-hidden="true" /> : null}
              <div><strong>{codes.length > 0 ? 'СМС получено' : isCurrent ? 'Ожидаем СМС' : 'СМС не получено'}</strong>{isCurrent && codes.length === 0 ? <span>Проверяем входящие автоматически</span> : null}</div>
            </div>
          ) : (
            <div className="sms-inbox__messages">{messages.map((message) => (
              <article key={message.id} className="sms-message">
                <div className="sms-message__head">
                  <time dateTime={message.receivedAt}>{formatDateTime(message.receivedAt)}</time>
                  {message.code ? <button type="button" className="sms-codeButton" onClick={() => handleCopy(item, message.code || '')} aria-label={`Скопировать код ${message.code}`}><SmsIcon name="copy" />Код {message.code}</button> : null}
                </div>
                <p>{message.text}</p>
              </article>
            ))}</div>
          )}
        </section> : null}

        <details className="sms-activeCard__details">
          <summary>Подробности номера</summary>
          <dl className="sms-activeCard__meta">
          <div>
            <dt>Куплен</dt>
            <dd>{formatDateTime(item.purchasedAt)}</dd>
          </div>
          <div>
            <dt>{isExpiredByTime ? 'Действовал до' : 'Действует до'}</dt>
            <dd>{formatDateTime(item.expiresAt)}</dd>
          </div>
          <div>
            <dt>Срок активации</dt>
            <dd>{formatActivationDuration(item.purchasedAt, item.expiresAt)}</dd>
          </div>
          <div>
            <dt>Стоимость</dt>
            <dd>{formatUsd(item.finalPrice)}</dd>
          </div>
          <div>
            <dt>Телефонный код страны</dt>
            <dd>{phone.countryCallingCode || '—'}</dd>
          </div>
          {item.operator ? (
            <div>
              <dt>Оператор</dt>
              <dd>{item.operator}</dd>
            </div>
          ) : null}
          {item.lastPolledAt ? (
            <div>
              <dt>Последняя проверка статуса</dt>
              <dd>{formatDateTime(item.lastPolledAt)}</dd>
            </div>
          ) : null}
          </dl>
        </details>

        {refundLabel ? (
          <div className={`sms-refundStatus ${item.refundOutcomeCode === 'refunded' ? 'is-complete' : ''}`}>
            <span>Возврат</span>
            <strong>{refundLabel}</strong>
          </div>
        ) : null}

        {!isMockMode && !isRefunded && isCurrent && (supportsLifecycleActions || canCancel) ? (
          <div className="sms-actions">
            {supportsLifecycleActions ? (
              <>
                <button
                  type="button"
                  className="u2-btn is-secondary is-sm"
                  onClick={() => runAction(item, 'request', requestAnotherSms)}
                  disabled={!canRequestAgain || isBusy}
                >
                  {pendingById[item.id] === 'request' ? '…' : 'Запросить ещё СМС'}
                </button>
                <button
                  type="button"
                  className="u2-btn is-secondary is-sm"
                  onClick={() => runAction(item, 'finish', finishActivation)}
                  disabled={!canFinish || isBusy}
                >
                  {pendingById[item.id] === 'finish' ? '…' : 'Завершить'}
                </button>
              </>
            ) : null}
            <button
              type="button"
              className="u2-btn is-danger is-sm"
              onClick={() => handleCancel(item)}
              disabled={!canCancel || cancelScheduled || isBusy}
            >
              {pendingById[item.id] === 'cancel' ? '…' : (cancelScheduled ? 'Отмена запланирована' : 'Отменить')}
            </button>
          </div>
        ) : null}

        {noticeById[item.id] ? <p className="is-positive" role="status">{noticeById[item.id]}</p> : null}
        {errorById[item.id] ? <p className="is-negative" role="alert">{errorById[item.id]}</p> : null}
      </article>
    );
  };

  const currentItems = items.filter((item) => (
    !FINAL_STATUSES.has(item.status) && getRemainingMs(item.expiresAt, now) > 0
  ));
  const completedItems = items.filter((item) => !currentItems.includes(item));

  return (
    <div className="sms-pageStack">
      <header className="sms-pageIntro">
        <div>
          <h1>Мои номера</h1>
          <p>Обычные активации, Voice verification и долгосрочная аренда в одном месте.</p>
        </div>
        <div className="sms-pageIntro__actions">
          <button
            type="button"
            className="u2-btn is-secondary is-sm"
            onClick={refreshAll}
            disabled={isRefreshingAll}
          >
            {isRefreshingAll ? 'Обновляем…' : 'Обновить все'}
          </button>
        </div>
      </header>

      <LongRentalNumbersSection />
      <VoiceVerificationNumbersSection />
      {loadError || isMockMode ? (
        <section className="sms-card sms-activeNotice">
          {loadError ? <p className="is-negative">{loadError}</p> : null}
          {isMockMode ? <p className="u2-muted">Демо-режим: действия с активациями недоступны. Покупка и управление работают в рабочем режиме.</p> : null}
        </section>
      ) : null}

      <section className="sms-numberGroup">
        <div className="sms-numberGroup__head">
          <h3>Действующие</h3>
          <p>Номера, у которых ещё не закончился срок активации.</p>
          <div className="sms-numberView" role="group" aria-label="Вид действующих номеров">
            <button type="button" aria-pressed={view === 'cards'} onClick={() => changeView('cards')}>Карточки</button>
            <button type="button" aria-pressed={view === 'table'} onClick={() => changeView('table')}>Таблица</button>
          </div>
        </div>
        {view === 'table' && currentItems.length > 0 ? <div className="sms-numberTableScroll" role="region" aria-label="Действующие номера — таблица" tabIndex={0}>
          <table className="sms-numberTable">
            <caption>Действующие номера · {currentItems.length}</caption>
            <thead><tr><th scope="col">Сервис</th><th scope="col">Страна</th><th scope="col">Номер</th><th scope="col">СМС</th><th scope="col">Осталось</th></tr></thead>
            <tbody>{currentItems.map(item => renderNumber(item, true))}</tbody>
          </table>
        </div> : <div className="sms-activeList">
          {currentItems.length > 0 ? currentItems.map(item => renderNumber(item)) : (
            <article className="sms-activeCard">
              <p className="u2-muted">Действующих номеров сейчас нет.</p>
            </article>
          )}
        </div>}
      </section>

      {completedItems.length > 0 ? (
        <section className="sms-numberGroup">
          <div className="sms-numberGroup__head">
            <h3>Завершённые</h3>
            <p>Истёкшие и отменённые номера с результатом получения СМС и возврата.</p>
          </div>
          <div className="sms-activeList">
            {completedItems.map(item => renderNumber(item))}
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default ActiveNumbers;
